/* eslint-disable global-require */
import { ConfigProvider } from 'antd';
import { Locale } from 'antd/lib/locale-provider';
import { Location, LocationDescriptor, parsePath } from 'history';
import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo
} from 'react';
import { IntlProvider as BaseIntlProvider } from 'react-intl';
import { Link as BaseLink, LinkProps, useHistory } from 'react-router-dom';
import { useGetSet } from 'react-use';
import useAsync from '../hooks/useAsync';

const locales: Record<
  string,
  {
    code: string;
    intlLocaleData: () => Promise<void>;
    messages: () => Promise<Record<string, string>>;
    antd: () => Promise<Locale>;
  }
> = {
  'zh-cn': {
    code: 'zh-cn',
    intlLocaleData: () => import('intl/locale-data/jsonp/zh-Hans-CN'),
    messages: async () => {
      const { default: messages } = await import(
        '../assets/locales/zh-cn.json'
      );
      return messages;
    },
    antd: async () => {
      const { default: antdLocale } = await import('antd/lib/locale/zh_CN');
      return antdLocale;
    },
  },
  en: {
    code: 'en',
    intlLocaleData: () => import('intl/locale-data/jsonp/en-US'),
    messages: async () => {
      const { default: messages } = await import('../assets/locales/en.json');
      return messages;
    },
    antd: async () => {
      const { default: antdLocale } = await import('antd/lib/locale/en_US');
      return antdLocale;
    },
  },
};

const isValidLocale = (locale: string) =>
  Object.keys(locales).some((i) => i === locale);

const defaultLocale = () => {
  const lang = navigator.language.toLowerCase() || 'zh-cn';
  return lang.substr(0, 2) === 'zh' ? 'zh-cn' : 'en';
};

const I18nContext = React.createContext<{
  locale: [string, Dispatch<SetStateAction<string>>];
}>({ locale: [defaultLocale(), () => {}] });

const LocaleProvider: React.FC = ({ children }) => {
  const history = useHistory();
  const { search } = history.location;

  const [get, set] = useGetSet<string>(() => {
    const params = new URLSearchParams(search);
    const value = params.get('locale');
    if (value && isValidLocale(value)) {
      return value;
    }
    return defaultLocale();
  });

  const setLocale = useCallback(
    (action: SetStateAction<string>) => {
      set(action);
      const params = new URLSearchParams(history.location.search);
      params.set('locale', get());
      history.replace(`?${params}`);
    },
    [get, set, history]
  );

  const locale = get();

  useEffect(() => {
    const params = new URLSearchParams(search);
    const value = params.get('locale');
    if (value !== locale) {
      if (value && isValidLocale(value)) {
        set(value);
      } else {
        params.set('locale', locale);
        history.replace(`?${params}`);
      }
    }
  }, [locale, search, history, set]);

  return (
    <I18nContext.Provider value={{ locale: [locale, setLocale] }}>
      {children}
    </I18nContext.Provider>
  );
};

export function useLocale(): [string, Dispatch<SetStateAction<string>>] {
  const i18n = useContext(I18nContext);
  return i18n.locale;
}

const IntlProvider: React.FC = ({ children }) => {
  const [locale] = useLocale();
  const { value } = useAsync(async () => {
    if (!window.Intl) {
      const intl = await import('intl');
      await locales[locale].intlLocaleData();
      window.Intl = intl.default;
    }
    const [messages, antdLocale] = await Promise.all([
      locales[locale].messages(),
      locales[locale].antd(),
    ]);
    return {
      locale,
      messages,
      antdLocale,
    };
  }, [locale]);

  if (!value) {
    return null;
  }
  return (
    <ConfigProvider locale={value.antdLocale}>
      <BaseIntlProvider locale={value.locale} messages={value.messages}>
        {children}
      </BaseIntlProvider>
    </ConfigProvider>
  );
};
export const I18nProvider: React.FC = ({ children }) => (
  <LocaleProvider>
    <IntlProvider>{children}</IntlProvider>
  </LocaleProvider>
);

const setLocale = (to: LocationDescriptor, locale: string) => {
  const location = typeof to === 'string' ? parsePath(to) : to;
  const params = new URLSearchParams(location.search);
  if (params.get('locale') === locale) {
    return to;
  }
  params.set('locale', locale);
  location.search = `?${params}`;
  return location;
};

export const Link: React.FC<LinkProps> = ({ to, ...rest }) => {
  const [locale] = useLocale();

  const location = useMemo(() => {
    if (typeof to === 'function') {
      return (location: Location) => setLocale(to(location), locale);
    }
    return setLocale(to, locale);
  }, [to, locale]);

  return <BaseLink {...rest} to={location} />;
};
